<template>
  <div class="container py-5">

    <div class="buttons">
      <button
        class="button is-primary"
        @click="adding = true">
        Create an Installation
      </button>
      <button
        class="button"
        @click="managingInstallationTypes = true">
        Manage Installation Types
      </button>
      <v-fade-transition>
        <button 
          class="button"
          v-if="selected.length > 0"
          @click="launchConfirmDelete()">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete {{ selected.length }} selected installations</span>
        </button>
      </v-fade-transition>
    </div>

    <article 
      v-if="!loading && installations.length == 0" 
      class="message is-info is-fullwidth mt-3">
      <div class="message-body">
        There are no installations.
      </div>
    </article>

    <div class="columns mt-3">

      <div class="column">
        <table class="table is-hoverable is-fullwidth">
          <thead>
            <tr>
              <th>
                <v-checkbox 
                  :value="allSelected" 
                  @input="toggleAllSelected($event)"/>
              </th>
              <th 
                class="text--nowrap"
                v-for="(header, headerIndex) in headers" 
                :key="headerIndex">
                {{ header.label }}
              </th>
              <th></th>
            </tr>
            <tr v-if="loading">
              <th :colspan="headers.length + 2" class="py-0 px-0 is-naked">
                <v-progress class="is-fullwidth" v-model="loading"/>
              </th>
            </tr>
          </thead>

          <tbody v-if="!loading && installations.length > 0">
            <tr 
              class="v-centered"
              v-for="installation in installations"
              :key="installation.id">

              <td>
                <v-checkbox 
                  :value="selected.includes(installation.id)" 
                  @input="toggleSelected(installation.id)"/>
              </td>

              <td>
                {{ installation.installation_name }}
              </td>

              <td>
                {{ installation.installation_type.installation_type_name }}
              </td>

              <td>
                {{ installation.unit.unit_display_name }}
              </td>

              <td>
                {{ installation.unit_price }}
              </td>

              <td>
                {{ installation.waste_pct }}
              </td>
  
              <td>
                <a 
                  class="mr-3"
                  v-if="installation.image != null"
                  @click.stop=""
                  :href="installation.image" 
                  alt="installation image"
                  target="_blank">
                  {{ installation.filename }}
                </a>
              </td>

              <td>
                <div class="buttons buttons-right">
                  <button
                    @click.stop="editInstallation(installation)" 
                    class="button is-small is-primary">
                    <span class="icon">
                      <i class="fas fa-edit"></i>
                    </span>
                    <span>Edit</span>
                  </button>
                  <button
                    @click.stop="launchConfirmDelete(installation.id)" 
                    class="button is-small">
                    <span class="icon">
                      <i class="fas fa-trash has-text-grey"></i>
                    </span>
                    <span>Delete</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

        </table>
      </div>
    </div>

    <add-installation-modal 
      :editingInstallation="editingInstallation"
      :value="adding"
      @input="toggleAdding"
    />

    <manage-installation-types-modal v-model="managingInstallationTypes"/>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VProgress from '@/components/base/VProgress'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import AddInstallationModal from '@/components/estimations/AddInstallationModal'
import ManageInstallationTypesModal from '@/components/estimations/ManageInstallationTypesModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VConfirmModal, 
    VPagination, 
    VCheckbox, 
    VProgress,
    VFadeTransition,
    AddInstallationModal,
    ManageInstallationTypesModal
  },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Installation Type' },
        { label: 'Unit Type' },
        { label: 'Unit Price'},
        { label: 'Waste %'},
        { label: 'Image' }
      ],
      adding: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this installation?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      editingInstallation: null,
      managingInstallationTypes: false
    }
  },
  computed: {
    ...mapState("installations", [
      "installations", "installationTypes", "loading", "pagination", 
      "allSelected", "selected"
    ]),
    ...mapState("units", ["units"])
  },
  watch: {
    'pagination.page'(page) {
      this.getInstallations({ page: page });
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("installations", [
      "getInstallations", "updateInstallation", "deleteInstallation", 
      "addImage", "toggleAllSelected", "getInstallationTypes"
    ]),
    ...mapMutations("installations", ["changePage", "toggleSelected"]),
    ...mapActions("units", ["getUnits"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      this.getInstallations();

      if (this.installationTypes.length == 0) {
        this.getInstallationTypes();
      }

      if (this.units.length == 0) {
        this.getUnits();
      }
    },
    editInstallation(installation) {
      this.editingInstallation = installation;
      this.adding = true;
    }, 
    toggleAdding(v) {
      if (v) {
        this.adding = true;
      } else {
        this.adding = false;
        this.editingInstallation = null;
      }
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteInstallation(id);
            this.confirmDelete.state = false;
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} installations?`;
        this.confirmDelete.state = true;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteInstallation(id);
          this.confirmDelete.state = false;
        }
        this.confirmDelete.message = 'Are you sure you want to delete this installation?';
        this.confirmDelete.state = true;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
</style>